import React, { useEffect, useState } from "react";
import { useLoaderData, useRevalidator } from "react-router-dom";
import { useBreakpoint, useDisclosure, useToast } from "@chakra-ui/react";
import client from "../../../../helpers/Client";
import urlToId from "../../../../helpers/UrlToId";
import EmployeeListCardView from "./EmployeeListCardView";
import EmployeeListTableView from "./EmployeeListTableView";
import ImprovedEmployeeTableView from "./ImprovedEmployeeTableView";

// Import modal components from the original table view
import {
  AddToTeamModal,
  CreateTeamModal,
  EditEmployeeModal,
  DeleteEmployeeModal,
  RemoveFromTeamModal
} from "./EmployeeListTableView";

export async function EmployeLoader() {
  const resp = await client.get("/employees/", { disableCache: true });
  return [resp.data]
}

export default function EmployeList() {
  const [data] = useLoaderData();
  const revalidator = useRevalidator();
  const toast = useToast();
  const [employeesData, setEmployeesData] = useState(data);

  // Modal states
  const addToTeamModalDisclosure = useDisclosure();
  const createTeamModalDisclosure = useDisclosure();
  const editEmployeeDisclosure = useDisclosure();
  const deleteEmployeeDisclosure = useDisclosure();
  const removeFromTeamModalDisclosure = useDisclosure();
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedMembership, setSelectedMembership] = useState(null);

  useEffect(() => {
    setEmployeesData(data);
  }, [data]);

  const patchEmployee = (updated) => {
    if (!updated || !updated.url) return;
    setEmployeesData((prev) => {
      const idx = prev.findIndex((e) => e.url === updated.url);
      if (idx === -1) return prev;
      const next = prev.slice();
      next[idx] = updated;
      return next;
    });
  };

  // Handler functions
  const handleAddToTeam = (employee) => {
    setSelectedEmployee(employee);
    addToTeamModalDisclosure.onOpen();
  };

  const handleEditEmployee = (employee) => {
    setSelectedEmployee(employee);
    editEmployeeDisclosure.onOpen();
  };

  const handleDeleteEmployee = (employee) => {
    setSelectedEmployee(employee);
    deleteEmployeeDisclosure.onOpen();
  };

  const handleCreateTeam = () => {
    createTeamModalDisclosure.onOpen();
  };

  const handleRemoveFromTeam = (employee, membership) => {
    setSelectedEmployee(employee);
    setSelectedMembership(membership);
    removeFromTeamModalDisclosure.onOpen();
  };

  // Remove from team callback
  const removeFromTeamCallback = (membership, disclosure) => {
    client
      .delete(`/members/${urlToId(membership.url)}/`)
      .then(async () => {
        try {
          const resp = await client.get(selectedEmployee.url, { disableCache: true });
          const updated = Array.isArray(resp?.data) ? resp.data[0] : resp.data;
          if (updated) patchEmployee(updated);
        } catch (_) { }
        toast({
          title: "Başarılı",
          description: `Personel takımdan çıkartıldı`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        disclosure.onClose();
      })
      .catch(() => {
        toast({
          title: "Hata",
          description: "Takımdan çıkarma işlemi başarısız oldu",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      });
  };

  return (
    <>
      <ImprovedEmployeeTableView
        employees={employeesData}
        onAddToTeam={handleAddToTeam}
        onEditEmployee={handleEditEmployee}
        onDeleteEmployee={handleDeleteEmployee}
        onCreateTeam={handleCreateTeam}
        onRemoveFromTeam={handleRemoveFromTeam}
      />

      {/* Modals */}
      <AddToTeamModal
        disclosure={addToTeamModalDisclosure}
        selectedEmployee={selectedEmployee}
        revalidator={revalidator}
        onEmployeeUpdated={patchEmployee}
      />
      <CreateTeamModal
        disclosure={createTeamModalDisclosure}
        revalidator={revalidator}
      />
      <EditEmployeeModal
        employee={selectedEmployee}
        disclosure={editEmployeeDisclosure}
        revalidator={revalidator}
      />
      <DeleteEmployeeModal
        employee={selectedEmployee}
        disclosure={deleteEmployeeDisclosure}
        revalidator={revalidator}
      />
      <RemoveFromTeamModal
        disclosure={removeFromTeamModalDisclosure}
        revalidator={revalidator}
        membership={selectedMembership}
        employee={selectedEmployee}
        callback={removeFromTeamCallback}
      />
    </>
  );
}
